import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  X, HeartPulse, Activity, Moon, Droplets, 
  FlaskConical, Check, Plus, AlertCircle, Sparkles 
} from 'lucide-react';
import { cn } from '../lib/utils';
import { BiomarkerLog, Language } from '../types';
import { triggerHaptic } from '../lib/haptic';
import { sounds } from '../lib/sound';
import { toast } from 'sonner';

interface BiomarkerLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveBiomarker: (log: Partial<BiomarkerLog>) => void;
  lang: Language;
  theme: 'light' | 'dark';
  skin?: string;
}

export const BiomarkerLogModal: React.FC<BiomarkerLogModalProps> = ({
  isOpen,
  onClose,
  onSaveBiomarker,
  lang,
  theme,
  skin = 'default'
}) => {
  const isUa = lang === 'ua';
  const isStalker = skin === 'stalker';

  const [tab, setTab] = useState<'vitals' | 'glucose' | 'blood_labs'>('vitals');

  // Vitals State
  const [hrv, setHrv] = useState<string>('');
  const [restingHr, setRestingHr] = useState<string>('');
  const [deepSleep, setDeepSleep] = useState<string>('');
  const [remSleep, setRemSleep] = useState<string>('');
  const [bpSystolic, setBpSystolic] = useState<string>('');
  const [bpDiastolic, setBpDiastolic] = useState<string>('');

  // Glucose State
  const [glucoseFasting, setGlucoseFasting] = useState<string>('');
  const [glucosePostprandial, setGlucosePostprandial] = useState<string>('');

  // Blood Labs State
  const [hba1c, setHba1c] = useState<string>('');
  const [hsCrp, setHsCrp] = useState<string>('');
  const [apob, setApob] = useState<string>('');
  const [vitaminD, setVitaminD] = useState<string>('');
  const [note, setNote] = useState<string>('');

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    triggerHaptic('SUCCESS');
    sounds.playPop();

    const payload: Partial<BiomarkerLog> = {
      timestamp: Date.now(),
      source: 'manual',
      note: note.trim() || undefined
    };

    if (hrv) payload.hrv_rmssd = parseFloat(hrv);
    if (restingHr) payload.resting_heart_rate = parseFloat(restingHr);
    if (deepSleep) payload.deep_sleep_minutes = parseFloat(deepSleep);
    if (remSleep) payload.rem_sleep_minutes = parseFloat(remSleep);
    if (bpSystolic) payload.blood_pressure_systolic = parseFloat(bpSystolic);
    if (bpDiastolic) payload.blood_pressure_diastolic = parseFloat(bpDiastolic);

    if (glucoseFasting) payload.fasting_glucose_mgdl = parseFloat(glucoseFasting);
    if (glucosePostprandial) payload.postprandial_glucose_mgdl = parseFloat(glucosePostprandial);

    if (hba1c) payload.hba1c_pct = parseFloat(hba1c);
    if (hsCrp) payload.hs_crp_mgl = parseFloat(hsCrp);
    if (apob) payload.apob_mgdl = parseFloat(apob);
    if (vitaminD) payload.vitamin_d_ngml = parseFloat(vitaminD);

    onSaveBiomarker(payload);
    toast.success(isUa ? 'Біомаркери успішно записано!' : 'Biomarkers logged successfully!');
    onClose();
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md overflow-y-auto">
        <motion.div
          initial={{ scale: 0.94, opacity: 0, y: 15 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.94, opacity: 0, y: 15 }}
          transition={{ type: "spring", stiffness: 350, damping: 28 }}
          className={cn(
            "relative w-full max-w-lg overflow-hidden rounded-3xl border shadow-2xl my-8",
            isStalker 
              ? "bg-black border-[#4ade80]/40 text-[#4ade80] font-mono" 
              : theme === 'light' 
                ? "bg-white border-zinc-200 text-zinc-900" 
                : "bg-zinc-900 border-zinc-800 text-zinc-50"
          )}
        >
          {/* Header Banner Accent */}
          <div className={cn(
            "h-2 w-full",
            isStalker 
              ? "bg-[#4ade80]" 
              : "bg-gradient-to-r from-emerald-500 via-teal-500 to-indigo-500"
          )} />

          {/* Modal Header */}
          <div className="p-6 pb-4 flex items-center justify-between border-b border-zinc-200/60 dark:border-zinc-800/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center border border-emerald-500/30 bg-emerald-500/10 text-emerald-500 shrink-0">
                <HeartPulse className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base sm:text-lg font-black uppercase tracking-wider">
                  {isStalker 
                    ? (isUa ? "ФІКСАЦІЯ БІОМЕТРИЧНИХ ДАНИХ" : "BIOMETRIC DOSIMETRY INPUT")
                    : (isUa ? "Швидкий запис біомаркерів" : "Biomarker & Lab Quick-Log")}
                </h3>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  {isUa ? "HRV, глюкоза, сон та лабораторні панелі" : "HRV, glucose, sleep & clinical lab panels"}
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-xl text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Sub-Tab Selectors */}
          <div className="px-6 pt-4 flex gap-2 border-b border-zinc-200/60 dark:border-zinc-800/80">
            {[
              { id: 'vitals', labelUa: 'Пульс & Сон', labelEn: 'Vitals & Sleep', icon: Activity },
              { id: 'glucose', labelUa: 'Глюкоза', labelEn: 'Glucose', icon: Droplets },
              { id: 'blood_labs', labelUa: 'Аналізи (Кров)', labelEn: 'Blood Labs', icon: FlaskConical },
            ].map(item => {
              const Icon = item.icon;
              const active = tab === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setTab(item.id as any)}
                  className={cn(
                    "flex items-center gap-1.5 pb-2.5 text-xs font-bold uppercase tracking-wider transition-all border-b-2 cursor-pointer",
                    active 
                      ? (isStalker ? "border-[#4ade80] text-[#4ade80]" : "border-emerald-500 text-emerald-500")
                      : "border-transparent text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                  )}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{isUa ? item.labelUa : item.labelEn}</span>
                </button>
              );
            })}
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            {tab === 'vitals' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">
                      HRV (rMSSD, ms)
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 68"
                      value={hrv}
                      onChange={e => setHrv(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-[var(--input-bg)] text-[var(--text)] text-sm font-semibold placeholder:italic focus:outline-emerald-500"
                    />
                    <span className="text-[10px] text-zinc-400 mt-0.5 block">{isUa ? "Норма: 45-85 ms" : "Optimal: 45-85 ms"}</span>
                  </div>

                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">
                      {isUa ? "Пульс спокою (bpm)" : "Resting HR (bpm)"}
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 54"
                      value={restingHr}
                      onChange={e => setRestingHr(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-[var(--input-bg)] text-[var(--text)] text-sm font-semibold placeholder:italic focus:outline-emerald-500"
                    />
                    <span className="text-[10px] text-zinc-400 mt-0.5 block">{isUa ? "Норма: 50-65 bpm" : "Optimal: 50-65 bpm"}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">
                      {isUa ? "Глибокий сон (хв)" : "Deep Sleep (min)"}
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 95"
                      value={deepSleep}
                      onChange={e => setDeepSleep(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-[var(--input-bg)] text-[var(--text)] text-sm font-semibold placeholder:italic focus:outline-emerald-500"
                    />
                    <span className="text-[10px] text-zinc-400 mt-0.5 block">{isUa ? "Ціль: >90 хв" : "Target: >90 min"}</span>
                  </div>

                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">
                      {isUa ? "REM сон (хв)" : "REM Sleep (min)"}
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 90"
                      value={remSleep}
                      onChange={e => setRemSleep(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-[var(--input-bg)] text-[var(--text)] text-sm font-semibold placeholder:italic focus:outline-emerald-500"
                    />
                    <span className="text-[10px] text-zinc-400 mt-0.5 block">{isUa ? "Ціль: >90 хв" : "Target: >90 min"}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">
                      {isUa ? "Тиск (Систолічний)" : "BP Systolic (mmHg)"}
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 118"
                      value={bpSystolic}
                      onChange={e => setBpSystolic(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-[var(--input-bg)] text-[var(--text)] text-sm font-semibold placeholder:italic focus:outline-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">
                      {isUa ? "Тиск (Діастолічний)" : "BP Diastolic (mmHg)"}
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 76"
                      value={bpDiastolic}
                      onChange={e => setBpDiastolic(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-[var(--input-bg)] text-[var(--text)] text-sm font-semibold placeholder:italic focus:outline-emerald-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {tab === 'glucose' && (
              <div className="space-y-4">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">
                    {isUa ? "Рівень цукру натще (mg/dL)" : "Fasting Blood Glucose (mg/dL)"}
                  </label>
                  <input
                    type="number"
                    placeholder="e.g. 86"
                    value={glucoseFasting}
                    onChange={e => setGlucoseFasting(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-[var(--input-bg)] text-[var(--text)] text-sm font-semibold placeholder:italic focus:outline-emerald-500"
                  />
                  <span className="text-[10px] text-zinc-400 mt-0.5 block">
                    {isUa ? "Оптимально: 72–90 mg/dL (4.0–5.0 mmol/L)" : "Optimal: 72–90 mg/dL (4.0–5.0 mmol/L)"}
                  </span>
                </div>

                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">
                    {isUa ? "Постпрандіальний цукор (1-2 год після їжі, mg/dL)" : "Postprandial Glucose (1-2h post meal, mg/dL)"}
                  </label>
                  <input
                    type="number"
                    placeholder="e.g. 115"
                    value={glucosePostprandial}
                    onChange={e => setGlucosePostprandial(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-[var(--input-bg)] text-[var(--text)] text-sm font-semibold placeholder:italic focus:outline-emerald-500"
                  />
                  <span className="text-[10px] text-zinc-400 mt-0.5 block">
                    {isUa ? "Ціль: <120 mg/dL (уникнення гліко-спайків)" : "Target: <120 mg/dL (avoiding spikes)"}
                  </span>
                </div>
              </div>
            )}

            {tab === 'blood_labs' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">
                      HbA1c (%)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="e.g. 5.1"
                      value={hba1c}
                      onChange={e => setHba1c(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-[var(--input-bg)] text-[var(--text)] text-sm font-semibold placeholder:italic focus:outline-emerald-500"
                    />
                    <span className="text-[10px] text-zinc-400 mt-0.5 block">{isUa ? "Оптимально: <5.3%" : "Optimal: <5.3%"}</span>
                  </div>

                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">
                      hs-CRP (mg/L)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      placeholder="e.g. 0.5"
                      value={hsCrp}
                      onChange={e => setHsCrp(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-[var(--input-bg)] text-[var(--text)] text-sm font-semibold placeholder:italic focus:outline-emerald-500"
                    />
                    <span className="text-[10px] text-zinc-400 mt-0.5 block">{isUa ? "Запалення: <0.8 mg/L" : "Inflammation: <0.8 mg/L"}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">
                      ApoB (mg/dL)
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 75"
                      value={apob}
                      onChange={e => setApob(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-[var(--input-bg)] text-[var(--text)] text-sm font-semibold placeholder:italic focus:outline-emerald-500"
                    />
                    <span className="text-[10px] text-zinc-400 mt-0.5 block">{isUa ? "Ціль: <80 mg/dL" : "Target: <80 mg/dL"}</span>
                  </div>

                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">
                      Vitamin D3 (ng/mL)
                    </label>
                    <input
                      type="number"
                      placeholder="e.g. 55"
                      value={vitaminD}
                      onChange={e => setVitaminD(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-[var(--input-bg)] text-[var(--text)] text-sm font-semibold placeholder:italic focus:outline-emerald-500"
                    />
                    <span className="text-[10px] text-zinc-400 mt-0.5 block">{isUa ? "Оптимально: 50-80" : "Optimal: 50-80"}</span>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">
                {isUa ? "Примітки або контекст (опціонально)" : "Clinical Notes / Context (optional)"}
              </label>
              <input
                type="text"
                placeholder={isUa ? "Наприклад: здано натщесерце, після сауни..." : "e.g. Morning lab draw, post-fasting..."}
                value={note}
                onChange={e => setNote(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-[var(--input-bg)] text-[var(--text)] text-xs placeholder:italic focus:outline-emerald-500"
              />
            </div>

            {/* Actions */}
            <div className="pt-2 flex items-center justify-end gap-2 border-t border-zinc-200/60 dark:border-zinc-800/80">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 rounded-xl text-xs font-bold uppercase tracking-wider text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors cursor-pointer"
              >
                {isUa ? "Скасувати" : "Cancel"}
              </button>
              <button
                type="submit"
                className={cn(
                  "px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all flex items-center gap-1.5 cursor-pointer shadow-md",
                  isStalker
                    ? "bg-[#4ade80] text-black hover:bg-[#4ade80]/90"
                    : "bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-500/20"
                )}
              >
                <Check className="w-4 h-4" />
                <span>{isUa ? "Зберегти" : "Save Biomarkers"}</span>
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
